import { createShortHash } from './utils';

/**
 * Extracts the access_token from URL search parameters (server-side).
 * Use this in server components and route handlers.
 *
 * @param searchParams - The searchParams object from Next.js server components
 * @returns The access token string, or undefined if not present
 */
export function getAccessToken(searchParams?: {
  [key: string]: string | string[] | undefined;
}): string | undefined {
  return searchParams?.['access_token'] as string | undefined;
}

/**
 * Extracts the access_token from URLSearchParams (client-side).
 * Use this with the result from useSearchParams() hook in client components.
 *
 * @param urlSearchParams - The URLSearchParams object from useSearchParams() hook
 * @returns The access token string, or null if not present
 */
export function getUrlAccessToken(
  urlSearchParams: URLSearchParams | ReadonlyURLSearchParams | null,
): string | null {
  return urlSearchParams?.get('access_token') ?? null;
}

// Type for ReadonlyURLSearchParams from Next.js
type ReadonlyURLSearchParams = {
  get(name: string): string | null;
};

/**
 * Creates a consistent hash key for access requests that works with both
 * slug-based routes (/share/org/instance) and ID-based routes (/p/projectId/publicationId)
 */
export function getAccessRequestKey(identifiers: {
  projectId?: string;
  publicationId?: string;
  organizationSlug?: string;
  instanceSlug?: string;
}): string {
  const { projectId, publicationId, organizationSlug, instanceSlug } =
    identifiers;

  let identifier: string;
  if (organizationSlug && instanceSlug) {
    identifier = `${organizationSlug}:${instanceSlug}`;
  } else if (projectId && publicationId) {
    identifier = `${projectId}:${publicationId}`;
  } else {
    return '';
  }

  return createShortHash(identifier);
}
